/**
 * Offline Queue Manager
 * Handles queueing API operations when offline and syncing when connection restored
 */

export interface QueuedOperation {
  id: string;
  type: 'annotation' | 'signature' | 'initial' | 'field' | 'delete' | 'comment';
  endpoint: string;
  method: 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body: any;
  timestamp: number;
  retryCount: number;
  sessionId: string;
  userId: string;
  status: 'pending' | 'syncing' | 'failed' | 'success';
  error?: string;
}

const STORAGE_KEY = 'offline_queue';
const STORAGE_QUOTA_WARNING = 4 * 1024 * 1024; // 4 MB (80% of 5MB)
const MAX_RETRIES = 3;

export class OfflineQueue {
  private queue: QueuedOperation[] = [];
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Add operation to queue
   */
  enqueue(operation: Omit<QueuedOperation, 'id' | 'timestamp' | 'retryCount' | 'status'>): string {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const queuedOp: QueuedOperation = {
      ...operation,
      id,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'pending',
    };

    this.queue.push(queuedOp);
    this.saveToStorage();
    this.notifyListeners();

    return id;
  }

  /**
   * Get all pending operations
   */
  getPendingOperations(): QueuedOperation[] {
    return this.queue.filter(op => op.status === 'pending');
  }

  /**
   * Get all operations
   */
  getAllOperations(): QueuedOperation[] {
    return [...this.queue];
  }

  /**
   * Get operation by ID
   */
  getOperation(id: string): QueuedOperation | undefined {
    return this.queue.find(op => op.id === id);
  }

  /**
   * Update operation status
   */
  updateStatus(id: string, status: QueuedOperation['status'], error?: string): void {
    const op = this.queue.find(op => op.id === id);
    if (op) {
      op.status = status;
      if (error) op.error = error;
      this.saveToStorage();
      this.notifyListeners();
    }
  }

  /**
   * Remove operation from queue
   */
  remove(id: string): void {
    const index = this.queue.findIndex(op => op.id === id);
    if (index !== -1) {
      this.queue.splice(index, 1);
      this.saveToStorage();
      this.notifyListeners();
    }
  }

  /**
   * Clear all operations
   */
  clear(): void {
    this.queue = [];
    this.saveToStorage();
    this.notifyListeners();
  }

  /**
   * Clear successful operations
   */
  clearSuccessful(): void {
    this.queue = this.queue.filter(op => op.status !== 'success');
    this.saveToStorage();
    this.notifyListeners();
  }

  /**
   * Increment retry count
   */
  incrementRetry(id: string): boolean {
    const op = this.queue.find(op => op.id === id);
    if (op) {
      op.retryCount++;
      this.saveToStorage();
      return op.retryCount < MAX_RETRIES;
    }
    return false;
  }

  /**
   * Get storage size in bytes
   */
  getStorageSize(): number {
    // Return 0 if not in browser environment
    if (typeof window === 'undefined') {
      return 0;
    }
    
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) return 0;
      return new Blob([data]).size;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Check if storage is near quota
   */
  isNearQuota(): boolean {
    return this.getStorageSize() > STORAGE_QUOTA_WARNING;
  }

  /**
   * Get storage stats
   */
  getStorageStats() {
    const size = this.getStorageSize();
    const sizeInMB = (size / (1024 * 1024)).toFixed(2);
    const percentUsed = ((size / STORAGE_QUOTA_WARNING) * 100).toFixed(1);
    
    return {
      size,
      sizeInMB,
      percentUsed: parseFloat(percentUsed),
      nearQuota: this.isNearQuota(),
      totalOperations: this.queue.length,
      pendingOperations: this.getPendingOperations().length,
    };
  }

  /**
   * Subscribe to queue changes
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  /**
   * Load queue from localStorage
   */
  private loadFromStorage(): void {
    // Skip if not in browser environment (SSR)
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (data) {
        this.queue = JSON.parse(data);
      }
    } catch (error) {
      this.queue = [];
    }
  }

  /**
   * Save queue to localStorage
   */
  private saveToStorage(): void {
    // Skip if not in browser environment (SSR)
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.queue));
      
      // Check quota silently
      this.isNearQuota();
    } catch (error) {
      console.error('[OfflineQueue] Error saving to storage:', error);
      
      // Check if quota exceeded
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        // Remove oldest operations to make room
        this.queue = this.queue.slice(-50); // Keep only last 50 operations
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(this.queue));
        } catch (e) {
        }
      }
    }
  }
}

// Singleton instance
export const offlineQueue = new OfflineQueue();
